/**
 * What both screens of a pairing agree on before a bridge key is handed over.
 *
 * The owner first publishes only a commitment to its key (`commitOwnerKey`) and shows the key after the other device has
 * answered. That order is what makes the comparison binding: whoever stands in between must fix the key it shows the
 * device before it has seen the owner's key, so it cannot search for a pair that gives the same emoji and has one try
 * per offer the person answers (docs/POST-QUANTUM.md, THREAT_MODEL.md). The emoji table and the wording are
 * `lib/nearby.ts`; this module only makes the indexes.
 */
import { toBase64Url } from './base64url';
import { parseGuestPublicKey, parseOwnerPublicKey } from './exchange';
import { COMMIT_LABEL, SAS_BITS_EACH, SAS_COUNT, SAS_LABEL, type ExchangeFlow } from './labels';
import { sha256 } from './primitives';

const encoder = new TextEncoder();

/** SHA-256 over the flow's commit label, a zero byte and the owner public key, as base64url: published before the key is shown. */
export async function commitOwnerKey(flow: ExchangeFlow, ownerPublicKey: string): Promise<string> {
  const key = parseOwnerPublicKey(ownerPublicKey);
  return toBase64Url(await sha256(encoder.encode(COMMIT_LABEL[flow]), Uint8Array.of(0), key));
}

/** The indexes (0–63) of the emoji for a pair of public keys: the first 24 bits of SHA-256 over a label and both, six bits each. */
export async function sasIndexes(flow: ExchangeFlow, ownerPublicKey: string, guestPublicKey: string): Promise<readonly number[]> {
  const owner = parseOwnerPublicKey(ownerPublicKey);
  const guest = parseGuestPublicKey(guestPublicKey);
  const digest = await sha256(encoder.encode(SAS_LABEL[flow]), Uint8Array.of(0), owner, guest);
  const bits = ((digest[0] ?? 0) << 16) | ((digest[1] ?? 0) << 8) | (digest[2] ?? 0);
  const mask = (1 << SAS_BITS_EACH) - 1;
  // the first three digest bytes are one 24-bit number; the four emoji take six bits each, from the top
  return Array.from({ length: SAS_COUNT }, (_, at) => (bits >> (SAS_COUNT * SAS_BITS_EACH - (at + 1) * SAS_BITS_EACH)) & mask);
}
